import type { RJSFSchema } from '@rjsf/utils';
import {
  isNoAvailableTemplatesSentinel,
  parseTitleWithId,
} from '@/features/campaigns/v2/Sms/Templates/smsTemplateListFlowMapper';
import { TopicChoice } from '../comuni-helper';
import { belongsToTopic } from './topicPrefix';

const TEMPLATE_FIELD_KEYS = new Set(['template', 'template_id', 'templateId']);

type SchemaProperty = {
  enum?: unknown[];
  enumNames?: unknown[];
  oneOf?: Array<{ const?: unknown; title?: string }>;
  [key: string]: unknown;
};

type ReactJsonSchemaForm = {
  formId?: string;
  title?: string;
  description?: string;
  schema?: RJSFSchema;
  uiSchema?: Record<string, unknown>;
};

type StepWithForm = {
  handler?: string;
  reactJsonSchemaForm?: ReactJsonSchemaForm;
  [key: string]: unknown;
};

export type FilterTemplateEnumsResult<T> = {
  params: T;
  hasMatchingTemplates: boolean;
};

/** Extract the template name from a connector enum/display label. */
export const templateNameFromEnumLabel = (label: string): string => {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;

  // WhatsApp-style: `name [lang] (uuid)`
  const languageBracketAt = trimmed.indexOf(' [');
  if (languageBracketAt > 0) {
    return trimmed.slice(0, languageBracketAt).trim();
  }

  // SMS / generic: `Name (id)` — fall back to bare string
  return parseTitleWithId(trimmed).name;
};

const labelBelongsToTopic = (label: string, topic: TopicChoice): boolean => {
  if (isNoAvailableTemplatesSentinel(label)) return false;
  const name = templateNameFromEnumLabel(label);
  if (!name || isNoAvailableTemplatesSentinel(name)) return false;
  return belongsToTopic(name, topic);
};

const filterPropertyOptions = (property: SchemaProperty, topic: TopicChoice): { property: SchemaProperty; keptCount: number } => {
  if (Array.isArray(property.oneOf)) {
    const filtered = property.oneOf.filter((option) => {
      const label = String(option.title ?? option.const ?? '');
      return labelBelongsToTopic(label, topic);
    });
    return {
      property: { ...property, oneOf: filtered },
      keptCount: filtered.length,
    };
  }

  if (!Array.isArray(property.enum)) {
    // No enum/oneOf options to filter — leave the field alone (not an empty match).
    return { property, keptCount: -1 };
  }

  const enumValues = property.enum.map(String);
  const enumNames = Array.isArray(property.enumNames) ? property.enumNames.map(String) : undefined;
  const nextEnum: string[] = [];
  const nextEnumNames: string[] = [];

  enumValues.forEach((value, index) => {
    if (isNoAvailableTemplatesSentinel(value)) return;

    const displayLabel = enumNames?.[index] ?? value;
    if (isNoAvailableTemplatesSentinel(displayLabel)) return;

    // Prefer human-facing label (enumNames / combined enum string) for topic match
    if (!labelBelongsToTopic(displayLabel, topic)) return;

    nextEnum.push(value);
    if (enumNames) nextEnumNames.push(displayLabel);
  });

  const next: SchemaProperty = { ...property, enum: nextEnum };
  if (enumNames) {
    next.enumNames = nextEnumNames;
  }
  return { property: next, keptCount: nextEnum.length };
};

const disableTemplateField = (uiSchema: Record<string, unknown> | undefined, fieldKey: string): Record<string, unknown> => {
  const next = { ...(uiSchema ?? {}) };
  const fieldUi = (next[fieldKey] as Record<string, unknown> | undefined) ?? {};
  next[fieldKey] = { ...fieldUi, 'ui:disabled': true };
  return next;
};

/**
 * Filter template enum/oneOf options on a single RJSF step form by wizard topic.
 * When a template field ends up with zero options, marks it `ui:disabled`.
 */
export const filterStepFormSchema = <T extends ReactJsonSchemaForm>(
  formSchema: T,
  topic: TopicChoice
): { formSchema: T; hasMatchingTemplates: boolean } => {
  const schema = formSchema.schema;
  const properties = (schema?.properties ?? {}) as Record<string, SchemaProperty>;
  if (!schema || Object.keys(properties).length === 0) {
    return { formSchema, hasMatchingTemplates: true };
  }

  let sawTemplateField = false;
  let keptTotal = 0;
  const nextProperties: Record<string, SchemaProperty> = { ...properties };
  let nextUiSchema = formSchema.uiSchema ? { ...formSchema.uiSchema } : undefined;

  for (const [fieldKey, property] of Object.entries(properties)) {
    if (!TEMPLATE_FIELD_KEYS.has(fieldKey) || !property) continue;

    const { property: filtered, keptCount } = filterPropertyOptions(property, topic);
    // keptCount < 0 → field has no enum/oneOf options; leave it unchanged and skip matching.
    if (keptCount < 0) {
      nextProperties[fieldKey] = filtered;
      continue;
    }

    sawTemplateField = true;
    nextProperties[fieldKey] = filtered;
    keptTotal += keptCount;

    if (keptCount === 0) {
      nextUiSchema = disableTemplateField(nextUiSchema, fieldKey);
    }
  }

  if (!sawTemplateField) {
    return { formSchema, hasMatchingTemplates: true };
  }

  return {
    formSchema: {
      ...formSchema,
      schema: { ...schema, properties: nextProperties },
      uiSchema: nextUiSchema ?? formSchema.uiSchema ?? {},
    },
    hasMatchingTemplates: keptTotal > 0,
  };
};

/**
 * Filter every step’s template enums in connector prepare `params` by wizard topic.
 */
export const filterTemplateEnumsByTopic = <T extends Record<string, StepWithForm>>(
  params: T,
  topic: TopicChoice
): FilterTemplateEnumsResult<T> => {
  let hasMatchingTemplates = true;
  let sawAnyTemplateField = false;
  const nextParams = { ...params } as T;

  for (const [stepKey, step] of Object.entries(params)) {
    if (!step?.reactJsonSchemaForm) continue;

    const schemaProps = (step.reactJsonSchemaForm.schema?.properties ?? {}) as Record<string, unknown>;
    const hadTemplateField = Object.keys(schemaProps).some((key) => TEMPLATE_FIELD_KEYS.has(key));
    const { formSchema, hasMatchingTemplates: stepHasMatches } = filterStepFormSchema(step.reactJsonSchemaForm, topic);

    if (hadTemplateField) {
      sawAnyTemplateField = true;
      if (!stepHasMatches) hasMatchingTemplates = false;
    }

    (nextParams as Record<string, StepWithForm>)[stepKey] = {
      ...step,
      reactJsonSchemaForm: formSchema,
    };
  }

  return {
    params: nextParams,
    hasMatchingTemplates: sawAnyTemplateField ? hasMatchingTemplates : true,
  };
};
